// What the one input ("Ask BusyBee or add a task") reads out of the text while it is typed:
// in the browser, free, never a model. The symbols the desktop quick panel knows (#project,
// p1..p4, 30m: desktop/taskText.js) and a day or a time: "today", "tomorrow" and the weekdays
// in the app's language and in English, "15:00", "3pm", "31.10.", "2026-10-31". Any of them
// makes the text look like a task, unless it asks something ("?").

import { parseTaskText, personName, takeNames } from '../../desktop/taskText';

// Accents and Arabic vowel marks off: "manana" is "mañana", "غدا" is "غدًا".
const fold = (s) => String(s || '').toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f\u064b-\u065f\u0670]/g, '');
// Punctuation a word may carry in a sentence ("tomorrow, at 3pm."): `light` keeps the dots a
// date or "p.m." end with, `bare` drops them too.
const light = (word) => word.replace(/^[("'«„]+|[)"'»“,;:!]+$/g, '');
const bare = (word) => light(word).replace(/\.+$/, '');

const MERIDIEM = /^(a\.?m\.?|p\.?m\.?)$/i;
const TIME = /^(\d{1,2})(?::(\d{2}))?(a\.?m\.?|p\.?m\.?)?$/i;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DOT_DATE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})?$/;
// Small words that only tie a day or a time to the sentence ("at 3pm", "o 15:00", "um 15 Uhr").
const LINKS = new Set(['at', 'on', 'by', 'o', 'v', 'vo', 'um', 'am', 'a', 'à', 'alle', 'às', 'w', 'в', 'во', 'на', 'le', 'el']);
const QUESTION = /[?？؟]/;

/** The words for today, tomorrow, the weekdays and repeating ("every day", "every Monday"),
 * in English and in `lang` (with `t`: the app's own "Every day", "Every week"... labels). */
export function dayWords(lang, t) {
  const today = new Set(['today', 'tonight']);
  const tomorrow = new Set(['tomorrow']);
  if (t) {
    // A label may explain itself in brackets ("کل (آنے والا)"): people type the word.
    const plain = (key) => fold(t(key).replace(/\s*\(.*?\)\s*/g, ' ').trim());
    today.add(plain('common.today'));
    tomorrow.add(plain('common.tomorrow'));
  }
  const weekdays = new Map();
  [...new Set(['en', lang].filter(Boolean))].forEach((code) => {
    for (let day = 0; day < 7; day += 1) {
      try {
        // January 4, 2026 is a Sunday: day 0 like Date.getDay().
        weekdays.set(fold(new Intl.DateTimeFormat(code, { weekday: 'long' }).format(new Date(2026, 0, 4 + day))), day);
      } catch { /* a language the browser does not know */ }
    }
  });
  // Repeating: whole phrases only ("every day", never a lone "daily", which titles use), and
  // "every <weekday>" with the language's "every" (the first word of its every-day/-week/-month).
  const phrases = [];
  const every = new Set(['every']);
  const add = (text, type) => {
    const words = fold(String(text || '')).split(/\s+/).filter(Boolean);
    if (!words.length) return;
    phrases.push({ words, type });
    if (words.length > 1) every.add(words[0]);
  };
  [['every day', 'daily'], ['every weekday', 'weekdays'], ['every week', 'weekly'], ['every other week', 'biweekly'],
    ['every two weeks', 'biweekly'], ['every month', 'monthly']].forEach(([text, type]) => add(text, type));
  if (t) {
    [['bots.repeat.daily', 'daily'], ['bots.repeat.weekdays', 'weekdays'], ['bots.repeat.weekly', 'weekly'],
      ['bots.repeat.type_biweekly', 'biweekly'], ['bots.repeat.monthly', 'monthly']].forEach(([key, type]) => {
      const text = t(key);
      if (text && text !== key) add(text, type);
    });
  }
  phrases.sort((a, b) => b.words.length - a.words.length);
  return { today, tomorrow, weekdays, repeats: { phrases, every } };
}

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

function readTime(word, next) {
  const m = word.match(TIME);
  if (!m) return null;
  const suffix = (m[3] || (next && MERIDIEM.test(next) ? next : '')).toLowerCase().replace(/\./g, '');
  // A bare number is a number, not a time: it needs minutes or am/pm.
  if (m[2] === undefined && !suffix) return null;
  let hours = Number(m[1]);
  const minutes = m[2] === undefined ? 0 : Number(m[2]);
  if (minutes > 59) return null;
  if (suffix) {
    if (hours < 1 || hours > 12) return null;
    if (suffix === 'pm' && hours < 12) hours += 12;
    if (suffix === 'am' && hours === 12) hours = 0;
  } else if (hours > 23) {
    return null;
  }
  return { time: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`, usedNext: !m[3] && !!suffix };
}

function readDate(word, now) {
  let m = word.match(ISO_DATE);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.getMonth() === Number(m[2]) - 1 ? d : null;
  }
  m = word.match(DOT_DATE);
  if (m) {
    const year = m[3] ? Number(m[3]) : now.getFullYear();
    let d = new Date(year, Number(m[2]) - 1, Number(m[1]));
    if (d.getMonth() !== Number(m[2]) - 1) return null;
    // "3.1." in December is next January.
    if (!m[3] && d < startOfDay(now)) d = new Date(year + 1, Number(m[2]) - 1, Number(m[1]));
    return d;
  }
  return null;
}

// Languages written without spaces put the day and the time together ("明天15:00",
// "金曜日10:00"): a day word followed by a number is two words.
function splitDay(token, dayList) {
  if (!/^\D+\d/.test(token)) return [token];
  for (let i = 1; i < token.length; i += 1) {
    if (/\d/.test(token[i]) && dayList.includes(fold(token.slice(0, i)))) return [token.slice(0, i), token.slice(i)];
  }
  return [token];
}

/**
 * Split a typed line into a task: { title, date (a local day) , time ("HH:MM"), project,
 * projectToken, priority, duration } and whether it looks like a task (isTask). A time
 * without a day is today when it is still ahead, else tomorrow.
 */
export function parseQuickInput(text, { projects = [], people = [], sections = [], labels = [], words, now = new Date() } = {}) {
  const raw = String(text || '');
  // Picked "#Project Name", "@Person", "/List", "+Label" first: none of their words is a day or a time.
  const named = takeNames(raw, { projects, people, sections, labels });
  const { today, tomorrow, weekdays, repeats } = words || dayWords('en');
  const dayList = [...today, ...tomorrow, ...weekdays.keys()];
  const tokens = named.rest.split(/\s+/).filter(Boolean).flatMap((token) => splitDay(token, dayList));
  const kept = [];
  let date = null;
  let time = null;
  let repeat = null;  // { type, days_of_week } (0 = Monday)
  const nextWeekday = (jsDay) => addDays(startOfDay(now), (jsDay - now.getDay() + 7) % 7);
  const drop = () => { if (kept.length && LINKS.has(fold(kept[kept.length - 1]))) kept.pop(); };

  for (let i = 0; i < tokens.length; i += 1) {
    // "@today", "@tomorrow": how the table's add row wrote days before (a day, not a person).
    const word = bare(tokens[i]).replace(/^@(?=\S)/, (m) => (/^@(today|tomorrow|nextweek)$/i.test(bare(tokens[i])) ? '' : m));
    const folded = fold(word);
    if (!repeat && repeats) {
      const hit = repeats.phrases.find(({ words: w }) => w.every((part, k) => tokens[i + k] && fold(bare(tokens[i + k])) === part));
      if (hit) {
        repeat = { type: hit.type, days_of_week: [] };
        drop();
        i += hit.words.length - 1;
        continue;
      }
      // "every Monday", "každý pondelok"; written together where words are ("毎週月曜日").
      const after = i + 1 < tokens.length ? fold(bare(tokens[i + 1])) : null;
      const glued = [...repeats.every].find((e) => folded.startsWith(e) && weekdays.has(folded.slice(e.length)));
      if ((repeats.every.has(folded) && after && weekdays.has(after)) || glued) {
        const jsDay = weekdays.get(glued ? folded.slice(glued.length) : after);
        repeat = { type: 'weekly', days_of_week: [(jsDay + 6) % 7] };
        if (!date) date = nextWeekday(jsDay);
        drop();
        if (!glued) i += 1;
        continue;
      }
    }
    const pair = i + 1 < tokens.length ? fold(`${word} ${bare(tokens[i + 1])}`) : null;
    if (!date && pair && (today.has(pair) || tomorrow.has(pair))) {  // "hari ini", "hôm nay"
      date = addDays(startOfDay(now), today.has(pair) ? 0 : 1);
      drop();
      i += 1;
      continue;
    }
    if (!date && (today.has(folded) || tomorrow.has(folded))) {
      date = addDays(startOfDay(now), today.has(folded) ? 0 : 1);
      drop();
      continue;
    }
    if (!date && weekdays.has(folded)) {
      const ahead = (weekdays.get(folded) - now.getDay() + 7) % 7;
      date = addDays(startOfDay(now), ahead);
      drop();
      continue;
    }
    const day = !date ? readDate(light(tokens[i]), now) || readDate(word, now) : null;
    if (day) {
      date = day;
      drop();
      continue;
    }
    const next = tokens[i + 1] && light(tokens[i + 1]);
    const clock = !time ? readTime(light(tokens[i]), next) || readTime(word, next) : null;
    if (clock) {
      time = clock.time;
      drop();
      if (clock.usedNext) i += 1;
      continue;
    }
    kept.push(tokens[i]);
  }

  if (!date && kept.some((w) => /^@nextweek$/i.test(w))) {  // the table's old "@nextweek": next Monday
    date = addDays(startOfDay(now), ((8 - now.getDay()) % 7) || 7);
    kept.splice(kept.findIndex((w) => /^@nextweek$/i.test(w)), 1);
  }
  const rest = parseTaskText(kept.join(' '), { projects, people, sections, labels });
  const project = named.project || rest.project;
  const person = named.person || rest.person;
  const section = named.section || rest.section;
  const pickedLabels = [...named.labels, ...rest.labels.filter((l) => !named.labels.includes(l))];
  if (repeat && !date) {
    // A repeating task starts on its first day: today, or Monday for working days at a weekend.
    const weekend = now.getDay() === 0 || now.getDay() === 6;
    date = repeat.type === 'weekdays' && weekend ? nextWeekday(1) : startOfDay(now);
  }
  if (time && !date) {
    const [h, m] = time.split(':').map(Number);
    const at = new Date(now);
    at.setHours(h, m, 0, 0);
    date = addDays(startOfDay(now), at > now ? 0 : 1);
  }
  const projectToken = named.project ? named.project.name : rest.projectToken;
  const personToken = named.person ? personName(named.person) : rest.personToken;
  const signals = !!(date || time || projectToken || personToken || section || pickedLabels.length || repeat || rest.priority || rest.duration);
  return {
    title: rest.title,
    date,
    time,
    project,
    projectToken,
    person,
    personToken,
    section,
    labels: pickedLabels,
    recurrence: repeat ? { type: repeat.type, interval: 1, days_of_week: repeat.days_of_week } : null,
    priority: rest.priority,
    duration: rest.duration,
    isTask: signals && !QUESTION.test(raw),
  };
}
